<template>
  <div :style="getElementStyle">
    <div id="app">
      <el-col
        :span="showLabel && !isDefaultPos ? 4 : data.description ? 20 : 24"
        ref="colRef"
        class="bottom-space"
      >
        <icons
          v-if="isIconRequired"
          :iconName="data.icon_name"
          class="mr-1"
        ></icons>
        <label for="text" :style="getLabelStyles">{{
          label || data.label
        }}</label>
        <span
          v-if="data.validations?.required"
          style="color: red; font-weight: bold"
        >
          *
        </span>
        <span v-if="data.description">
          <i class="el-icon-info" :title="data.description"></i>
        </span>
      </el-col>

      <!-- Text Input -->
      <el-input
        v-model="form[data.key]"
        v-if="!data.validations?.mapViewRequired"
        :readonly="true"
        @change="applyFormRules"
      ></el-input>

      <!-- Map Section -->
      <div
        v-else
        class="map-container"
        style="border-radius: 10px; flex-grow: 1"
      >
        <div class="mapResource" v-if="data.validations?.AllowEdit">
          <el-input
            v-if="!isView"
            v-model="address"
            placeholder="Enter Address"
          ></el-input>
          <el-button
            v-if="!isView"
            @click="getCoordinates(address)"
            type="primary"
          >
            Search Address
          </el-button>
        </div>
        <!-- Cordninates shown in view mode -->
        <div
          class="coordinates-inputs"
          v-if="coordinates && coordinates.length === 2"
        >
          <el-input
            v-if="isView"
            :value="combinedCoordinates"
            :readonly="true"
            placeholder="Latitude, Longitude"
          >
          </el-input>
          <p v-else>
            {{ "Saved Location : " + "(" + combinedCoordinates + ")" }}
          </p>
        </div>
        <a :href="googleMapsUrl" target="_blank" rel="noopener noreferrer">
          <el-button type="primary"> Open in Google Maps </el-button>
        </a>
            <l-map
            ref="mapRef"
            :zoom="zoom"
            :center="center"
            style="height: 100%"
             @click="onMapClick"
           
          >

          <l-tile-layer :url="url"></l-tile-layer>
          <l-marker :lat-lng="coordinates" :icon="markerIcon" ></l-marker>

          <!-- Geofence circle -->
          <l-circle
            v-if="
              data.validations?.geoFenceCoordinates &&
              data.validations.geoFenceCoordinates.length === 2
            "
            :lat-lng="data.validations.geoFenceCoordinates"
            :radius="data.validations.geoFenceRadius || 0"
            :color="'red'"
            :fill-color="'red'"
            :fill-opacity="0.3"
          ></l-circle>
        </l-map>
      </div>
    </div>
  </div>
</template>

<script>
import { LMap, LTileLayer, LMarker, LCircle } from "vue2-leaflet";
import { Icon } from "leaflet";
import "leaflet/dist/leaflet.css";
import axios from "axios";
import userPermissionsHelper from "@/mixins/userPermissionsHelper";

export default {
  name: "App",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LCircle,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    form: {
      type: Object,
      required: true,
    },
    isView: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "",
    },
    isIconRequired: {
      type: Boolean,
      default: false,
    },
  },
  mixins: [userPermissionsHelper],
  data() {
    return {
      address: "",
      zoom: 10,
      showLabel: true,
      isDefaultPos: true, // Fixed typo
      center: [17.4065, 78.4772], // default center coordinates
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      coordinates: [17.4065, 78.4772],
      markerIcon: new Icon({
        iconUrl: require("leaflet/dist/images/marker-icon.png"),
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
        shadowSize: [41, 41],
      }),
    };
  },
  computed: {
    combinedCoordinates() {
      return `${this.coordinates[0]}, ${this.coordinates[1]}`;
    },
    googleMapsUrl() {
      if (!this.coordinates || this.coordinates.length !== 2) return "#";
      const [lat, lng] = this.coordinates;
      return `https://www.google.com/maps?q=${lat},${lng}`;
    },
    parsedCoordinates() {
      const value = this.form[this.data.key];
      if (!value) return null;

      const parts = value.split(",").map((part) => parseFloat(part.trim()));
      if (parts.length === 2 && parts.every((num) => !isNaN(num))) {
        return parts;
      }
      this.$emit("applyFormRules");
      return null;
    },
    getLabelStyles() {
      return this.data.styles?.label_color
        ? `color: ${this.data.styles.label_color}`
        : "font-weight: 700";
    },
    getElementStyle() {
      return {
        display: "flex",
        flexDirection: "column",
        borderRadius: "50px",
        padding: "10px",
        backgroundColor: "#f9f9f9",
      };
    },
  },
  watch: {
    parsedCoordinates: {
      handler(newVal) {
        if (newVal) {
          this.coordinates = [...newVal];
          this.center = [...newVal];
        }
      },
      immediate: true,
    },
  },

  methods: {
    async onMapClick(e) {
    const { lat, lng } = e.latlng;

    this.coordinates = [lat, lng];
    this.center = [lat, lng];
    this.$set(this.form, this.data.key, `${lat}, ${lng}`);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`
      );
      const data = await response.json();

      const addr = data.address;
      const shortAddress = [
        addr.suburb || "",
        addr.city || addr.town || addr.village || "",
        addr.state || "",
      ]
        .filter(Boolean)
        .join(", ");

      this.address = `${shortAddress} (${lat.toFixed(5)}, ${lng.toFixed(5)})`;
      this.$emit("applyFormRules");
    } catch (error) {
      console.error("Error during reverse geocoding:", error);
      this.address = `${lat}, ${lng}`;
    }
  },
     applyFormRules() {
      this.$emit("applyFormRules");
    },
    async getCoordinates(address) {
      try {
        if (!address) {
          console.error("Address is required");
          return;
        }

        const config = {
          params: {
            address,
            sensor: true,
            key: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
          },
        };

        const response = await axios.get(
          "https://maps.googleapis.com/maps/api/geocode/json",
          config
        );

        if (response.data?.results?.[0]?.geometry?.location) {
          const { lat, lng } = response.data.results[0].geometry.location;
          this.coordinates = [lat, lng];
          this.center = [lat, lng];
          this.$set(this.form, this.data.key, `${lat}, ${lng}`);
        } else {
          console.error("Invalid coordinates response:", response.data);
        }
      } catch (error) {
        console.error("Error fetching coordinates:", error);
      }
    },
    getUserLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            this.coordinates = [
              position.coords.latitude,
              position.coords.longitude,
            ];
            const coordString = `${this.coordinates[0]}, ${this.coordinates[1]}`;
            this.center = this.coordinates;
            this.$set(this.form, this.data.key, coordString);

            try {
              const response = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${this.coordinates[0]}&lon=${this.coordinates[1]}`
              );
              const data = await response.json();
              // Short address
              const addr = data.address;
              const shortAddress = [
                addr.suburb || "",
                addr.city || addr.town || addr.village || "",
                addr.state || "",
              ]
                .filter(Boolean)
                .join(", ");
              this.address = `${shortAddress} (${coordString})`;
              this.$emit("applyFormRules");
            } catch (error) {
              console.error("Error during reverse geocoding:", error);
              this.address = coordString;
            }
          },
          (error) => {
            console.error("Error getting location:", error);
          }
        );
      } else {
        console.error("Geolocation is not supported by this browser.");
      }
    },
  },
  mounted() {
    delete Icon.Default.prototype._getIconUrl;
    Icon.Default.mergeOptions({
      iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
      iconUrl: require("leaflet/dist/images/marker-icon.png"),
      shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
    });
    // this.getUserLocation();
    if (!this.parsedCoordinates && !this.isView) {
      this.getUserLocation();
    }
  },
};
</script>

<style scoped>
#app {
  border-radius: 50px;
  display: flex;
  flex-direction: column;
}

.map-container {
  flex-grow: 1;
  margin: 10px 0;
}

.mapResource {
  display: flex;
  justify-content: center;
  flex-direction: column;
}

.bottom-space {
  margin-bottom: 2px;
}

.el-button {
  border: none;
  background-color: cadetblue;
  color: white;
  padding: 5px 15px;
  border-radius: 15px;
  margin-top: 5px;
}

.el-icon-info {
  color: cadetblue;
  cursor: pointer;
}

.vue2leaflet-map {
  height: 100%;
  width: 100%;
  position: absolute !important;
}

.el-button {
  margin-bottom: 10px;
}
</style>
